import {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react'

import AdminNavBar from './AdminNavBar'
import ReportNavBar from './ReportNavBar'

interface Unit {
	id: number
	unit: string
	active: boolean
	selected: boolean
}

interface Location {
	id: number;
	short_name: string;
	long_name: string;
}

interface LoggedInUser {
	id: number;
	first_name: string;
	last_name: string;
	location: string;
	unit: string;
	logged_in_date: Date;
	logged_in_time: Date;
}

const ReportLoggedInUsers = () => {
	var locationRef = useRef<HTMLSelectElement>(null)
	var unitRef = useRef<HTMLSelectElement>(null)

	const [loggedInUsers, setLoggedInUsers] = useState<LoggedInUser[]>([])
	const [filteredUsers, setFilteredUsers] = useState<LoggedInUser[]>([])
	const [units, setUnits] = useState<Unit[]>([{ unit: "", id: 0, active: false, selected: false }])
	const [locations, setLocations] = useState<Location[]>([])

	const changeLocation = (e: ChangeEvent<HTMLSelectElement>) => {
		// TODO: update units to only be for location, and set to all if all locations
		
		filterUsers()
	}

	const changeUnit = (e: ChangeEvent<HTMLSelectElement>) => {
		filterUsers()
	}

	const filterUsers = ()  => {
		if (locationRef.current?.value === "" && unitRef.current?.value === "") {
			setFilteredUsers(loggedInUsers)
		} else if (locationRef.current?.value === "" && unitRef.current?.value !== "") {
			getUsersByUnit()
		} else if (locationRef.current?.value !== "" && unitRef.current?.value === "") {
			getUsersByLocation()
		} else if (locationRef.current?.value !== "" && unitRef.current?.value !== "") {
			getUsersByLocationAndUnit()
		}
	}

	const getUsersByUnit = () => {
		var users:LoggedInUser[] = []
		var unitName = ""
		for (var i = 0; i < units.length; i++) {
			if (units[i].id.toString() === unitRef.current?.value) {
				unitName = units[i].unit

				break
			}
		}
		for (var i = 0; i < loggedInUsers.length; i++) {
			if (loggedInUsers[i].unit === unitName) {
				users.push(loggedInUsers[i])
			}
		}
		setFilteredUsers(users)
	}

	const getUsersByLocation = () => {
		var users:LoggedInUser[] = []
		const locationID = locationRef.current?.value
		var locationName = ""
		for (var j = 0; j < locations.length; j++) {
			if (locations[j].id.toString() === locationID) {
				locationName = locations[j].short_name + " - " + locations[j].long_name
				break
			}
		}

		for (var i = 0; i < loggedInUsers.length; i++) {
			if (loggedInUsers[i].location === locationName) {
				users.push(loggedInUsers[i])
			}
		}
		setFilteredUsers(users)
	}

	const getUsersByLocationAndUnit = () => {
		var users:LoggedInUser[] = []
		const locationID = locationRef.current?.value
		var locationName = ""
		var unitName = ""

		for (var j = 0; j < locations.length; j++) {
			if (locations[j].id.toString() === locationID) {
				locationName = locations[j].short_name + " - " + locations[j].long_name
				break
			}
		}

		for (var i = 0; i < units.length; i++) {
			if (units[i].id.toString() === unitRef.current?.value) {
				unitName = units[i].unit
				break
			}
		}

		for (var i = 0; i < loggedInUsers.length; i++) {
			if (loggedInUsers[i].location === locationName && loggedInUsers[i].unit === unitName) {
				users.push(loggedInUsers[i])
			}
		}
		setFilteredUsers(users)
	}

	const getUnits = useCallback(async () => {
		const response = await fetch(process.env.REACT_APP_API + "/api/units")
		const results = await response.json()
		var localUnits: Unit[] = []
		for (var i= 0; i < results.length; i++) {
			var unit:Unit = {id: results[i].id, unit:results[i].unit, active:results[i].active, selected:false}
			localUnits.push(unit)
		}
		setUnits(localUnits)
	}, [setUnits])

	const getLocations = useCallback(async () => {
		const response = await fetch(process.env.REACT_APP_API + "/api/locations")
		const results = await response.json()
		setLocations(results)
	}, [setLocations])

	useEffect(() => {
		const fetchLoggedInUsers = async () => {
			try {
				const response = await fetch(process.env.REACT_APP_API+"/api/reportLoggedInUsers")
				const results = await response.json()
				for (var i = 0; i < results.length; i++) {
					// add 1 day to results[i].logged_in_date
					results[i].logged_in_date = new Date(results[i].logged_in_date)
					results[i].logged_in_date.setDate(results[i].logged_in_date.getDate() + 1) // adjust for timezone
					// add 1 hour to results[i].logged_in_time
					results[i].logged_in_time = new Date(results[i].logged_in_time)
					results[i].logged_in_time.setHours(results[i].logged_in_time.getHours() + 1) // adjust for timezone
				}

				setLoggedInUsers(results)
				setFilteredUsers(results)
			} catch (error) {
				console.log(error)
			}
		}
	
		fetchLoggedInUsers()
		getLocations()
		getUnits()
	}, [])

	const getPDF = async () => {
		try {
			const response = await fetch(process.env.REACT_APP_API+"/api/pdfLoggedInUsers")
			const blob = await response.blob()
			const url = window.URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href =
				url
			a.download = 'logged_in_users.pdf'
			document.body.appendChild(a)
			a.click()
			a.remove()
		}
		catch (error) {
			console.log(error)
		}
	}
	
	return (
		<div className="resilire">
			<AdminNavBar />
			<ReportNavBar />
			<br />
			<label htmlFor="locations">Location</label>
			<select id="locations" name="locations" ref={locationRef} onChange={changeLocation}>
				<option value="">All Locations</option>
				{
					locations.map((location) => (
						<option value={location.id} key={location.id}>{location.short_name} - {location.long_name}</option>
					))
				}
			</select>

			<label htmlFor="units">Unit</label>
			<select id="units" name="units" ref={unitRef} onChange={changeUnit}>
				<option value="">All Units</option>
				{
					units.map((unit) => (
						<option value={unit.id} key={unit.id}>{unit.unit}</option>
					))
				}
			</select>
			<br />
			<table id="logged_in_users">
				<thead><tr><th colSpan={5}>Clocked in Users</th></tr></thead>
				<tbody>
					<tr><th>Name</th><th>Location</th><th>Unit</th><th>Clock in Date</th><th>Clock in time</th></tr>
					{filteredUsers.map((user) => (
						<tr key={user.id}>
							<td>{user.first_name} {user.last_name}</td>
							<td>{user.location}</td>
							<td>{user.unit}</td>
							<td>{new Date(user.logged_in_date).toLocaleDateString()}</td>
							<td>{new Date(user.logged_in_time).toLocaleTimeString()}</td>
						</tr>
					))}
				</tbody>
			</table>
			<button onClick={getPDF}>Download</button>
		</div>
	)
};

export default ReportLoggedInUsers;
